import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, redirect, useRouteContext } from '@tanstack/react-router';
import {
  Alert,
  Button,
  Drawer,
  Dropdown,
  Empty,
  Flex,
  Grid,
  Input,
  message,
  Modal,
  Result,
  Select,
  Tag,
  Typography,
} from 'antd';
import Gleap from 'gleap';
import { Ellipsis, Phone, PhoneOff, RotateCw, Star } from 'lucide-react';
import { useMemo, useState } from 'react';

import {
  getGetTwilioAccountQueryKey,
  getListTwilioPhoneNumbersQueryKey,
  getSearchAvailableTwilioNumbersQueryKey,
  useAssignTwilioPhoneNumber,
  useDeactivateTwilioPhoneNumber,
  useGetTwilioAccount,
  useListOrganizationMemberships,
  useListTwilioPhoneNumbers,
  useProvisionTwilioAccount,
  useProvisionTwilioPhoneNumber,
  useSearchAvailableTwilioNumbers,
  useSetDefaultTwilioPhoneNumber,
} from '../../../../api';
import { buildInfo } from '../../../../build-info';
import { usePermissions } from '../../../../modules/account/hooks';
import { A2pRegistrationDrawer } from '../../../../modules/twilio/components/a2p-registration-drawer';
import { A2pStatusTimeline } from '../../../../modules/twilio/components/a2p-status-timeline';
import { US_STATE_CODE_BY_LOOKUP } from '../../../../modules/twilio/constants';
import { SettingsLayoutContent } from '../../../../ui/components/layout';
import { List } from '../../../../ui/components/list';
import { Loader } from '../../../../ui/components/loader';
import { Record } from '../../../../ui/components/record';
import { formatPhoneNumber } from '../../../../ui/utils/phone-number';

type SearchCriteria = 'locality' | 'areaCode';

const isA2pEnabled = import.meta.env.VITE_ENV === 'production';

/** Twilio returns locality + region (US state); show like "Morrisons Cross Roads, AL" for every search mode. */
function formatAvailableNumberLocation(
  locality?: string | null,
  region?: string | null,
): string | undefined {
  const loc = locality?.trim();
  const reg = region?.trim();
  if (loc && reg) {
    return `${loc}, ${reg.toUpperCase()}`;
  }
  if (reg) {
    return reg.toUpperCase();
  }
  if (loc) {
    return loc;
  }
  return undefined;
}

function formatAvailableNumberPrice(
  monthlyPrice?: string | null,
  priceUnit?: string | null,
): string {
  const price = monthlyPrice?.trim();
  if (!price) {
    return 'N/A';
  }
  const unit = priceUnit?.trim().toUpperCase();
  if (unit === 'USD') {
    return `$${price}/m`;
  }
  return unit ? `${price} ${unit}/m` : `${price}/m`;
}

const SettingsPhoneRoute = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization, selectedMembership } = useRouteContext({
    from: '/_authenticated/_app',
  });
  const { can } = usePermissions();
  const canEditPhone = can('phone', 'edit');
  const canBuyNumbers = can('phone', 'buyNumbers');
  const screens = Grid.useBreakpoint();
  const stackTwilioNumberActions = screens.sm !== true;
  const organizationId = selectedOrganization.id;

  const [buyDrawerOpen, setBuyDrawerOpen] = useState(false);
  const [a2pDrawerOpen, setA2pDrawerOpen] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState<SearchCriteria>('locality');
  const [searchInput, setSearchInput] = useState('');
  const [submitted, setSubmitted] = useState<{
    criteria: SearchCriteria;
    value: string;
    nonce: number;
  } | null>(null);

  const accountQuery = useGetTwilioAccount(organizationId);
  const twilioAccount = accountQuery.data?.data.item;

  const numbersQuery = useListTwilioPhoneNumbers(organizationId, { take: 100 });
  const numbers = numbersQuery.data?.data.items?.filter((n) => !n.deactivatedAt);

  const membersQuery = useListOrganizationMemberships(organizationId);

  const members = useMemo(
    () => membersQuery.data?.data.items?.filter((m) => !m.disabledAt && !m.deletedAt),
    [membersQuery.data],
  );

  const submittedStateCode =
    submitted?.criteria === 'locality'
      ? US_STATE_CODE_BY_LOOKUP[submitted.value.toLowerCase().replace(/[^a-z]/g, '')]
      : undefined;
  const submittedSearchParams = submitted
    ? {
        countryCode: 'US',
        inLocality:
          submitted.criteria === 'locality' && !submittedStateCode ? submitted.value : undefined,
        inRegion: submittedStateCode,
        areaCode: submitted.criteria === 'areaCode' ? submitted.value : undefined,
      }
    : undefined;
  const searchQuery = useSearchAvailableTwilioNumbers(organizationId, submittedSearchParams, {
    query: {
      enabled: buyDrawerOpen && submitted !== null,
      queryKey:
        submitted === null
          ? getSearchAvailableTwilioNumbersQueryKey(organizationId)
          : [
              ...getSearchAvailableTwilioNumbersQueryKey(organizationId, submittedSearchParams),
              submitted.nonce,
            ],
    },
  });
  const available = searchQuery.data?.data.items ?? [];
  const normalizedInput =
    searchCriteria === 'areaCode' ? searchInput.replace(/\D/g, '').slice(0, 3) : searchInput.trim();
  const isValidInput =
    searchCriteria === 'areaCode' ? normalizedInput.length === 3 : normalizedInput.length >= 2;
  const isStale = submitted !== null && normalizedInput !== submitted.value;
  const closeBuyDrawer = () => {
    setBuyDrawerOpen(false);
    setSearchInput('');
    setSearchCriteria('locality');
    setSubmitted(null);
    queryClient.removeQueries({
      queryKey: getSearchAvailableTwilioNumbersQueryKey(organizationId),
      exact: false,
    });
  };

  const provisionAccount = useProvisionTwilioAccount({
    mutation: {
      onSuccess: () => {
        message.success('Phone & Voicemail enabled');
        queryClient.invalidateQueries({ queryKey: getGetTwilioAccountQueryKey(organizationId) });
        queryClient.invalidateQueries({
          queryKey: getListTwilioPhoneNumbersQueryKey(organizationId),
        });
      },
      onError: () => {
        message.error('Could not enable Twilio');
      },
    },
  });

  const provisionNumber = useProvisionTwilioPhoneNumber({
    mutation: {
      onSuccess: () => {
        message.success('Number purchased');
        queryClient.invalidateQueries({
          queryKey: getListTwilioPhoneNumbersQueryKey(organizationId),
        });
        closeBuyDrawer();
      },
      onError: (error: unknown) => {
        // The $1.15 setup fee is charged before the number is bought: a declined card buys nothing.
        const response = (error as { response?: { status?: number; data?: { code?: string } } })?.response;
        if (response?.status === 402 && response.data?.code === 'PAYMENT_FAILED') {
          message.error('Your card was declined, so the number was not bought. Update your card and try again.');
          return;
        }
        message.error('Could not purchase number');
      },
    },
  });

  const assignNumber = useAssignTwilioPhoneNumber({
    mutation: {
      onSuccess: () => {
        message.success('Assignment updated');
        queryClient.invalidateQueries({
          queryKey: getListTwilioPhoneNumbersQueryKey(organizationId),
        });
      },
      onError: () => {
        message.error('Could not update assignment');
      },
    },
  });

  const deactivateNumber = useDeactivateTwilioPhoneNumber({
    mutation: {
      onSuccess: () => {
        message.success('Number deactivated');
        queryClient.invalidateQueries({
          queryKey: getListTwilioPhoneNumbersQueryKey(organizationId),
        });
      },
      onError: () => {
        message.error('Could not deactivate number');
      },
    },
  });

  const setDefaultNumber = useSetDefaultTwilioPhoneNumber({
    mutation: {
      onSuccess: () => {
        message.success('Default number updated');
        queryClient.invalidateQueries({
          queryKey: getListTwilioPhoneNumbersQueryKey(organizationId),
        });
      },
      onError: () => message.error('Could not update default number'),
    },
  });

  const memberOptions = useMemo(
    () =>
      members?.map((m) => ({
        value: m.id,
        label: `${
          `${m.user?.firstName ?? ''} ${m.user?.lastName ?? ''}`.trim() || m.user?.email || 'Member'
        }${m.id === selectedMembership.id ? ' (you)' : ''}`,
      })),
    [members, selectedMembership.id],
  );

  if (accountQuery.isPending || numbersQuery.isPending) {
    return (
      <SettingsLayoutContent
        title="Phone & Voicemail"
        description="Manage Twilio numbers for your workspace."
      >
        <Loader />
      </SettingsLayoutContent>
    );
  }

  if (accountQuery.isError) {
    return (
      <SettingsLayoutContent
        title="Phone & Voicemail"
        description="Manage Twilio numbers for your workspace."
      >
        <Result
          status="error"
          title="Failed to load"
          extra={
            <Button icon={<RotateCw size="1em" />} onClick={() => accountQuery.refetch()}>
              Try again
            </Button>
          }
        />
      </SettingsLayoutContent>
    );
  }

  if (!twilioAccount) {
    return (
      <SettingsLayoutContent
        title="Phone & Voicemail"
        description="Enable calling and SMS for this workspace."
      >
        <Empty
          description="Set up Phone & Voicemail to buy numbers, send SMS, and place browser calls."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button
            type="primary"
            disabled={!canEditPhone}
            loading={provisionAccount.isPending}
            onClick={() => {
              provisionAccount.mutate({ organizationId });
            }}
          >
            Enable Phone
          </Button>
        </Empty>
      </SettingsLayoutContent>
    );
  }

  const runSearch = () => {
    if (!isValidInput) {
      return;
    }
    setSubmitted((prev) => ({
      criteria: searchCriteria,
      value: normalizedInput,
      nonce: (prev?.nonce ?? 0) + 1,
    }));
  };

  return (
    <>
      <SettingsLayoutContent
        title="Phone & Voicemail"
        description="Buy Twilio numbers, assign them to team members, and manage SMS and voice."
        action={
          <Button type="primary" disabled={!canBuyNumbers} onClick={() => setBuyDrawerOpen(true)}>
            Buy Number
          </Button>
        }
      >
        {isA2pEnabled &&
          (twilioAccount.a2pStatus === 'not_started' ? (
            <Alert
              style={{ marginBottom: 24 }}
              showIcon
              type="warning"
              title="Register for US texting (A2P 10DLC)"
              description={
                <Flex vertical gap={12} align="flex-start">
                  Submit business and messaging details so Twilio can register your brand for US
                  application-to-person messaging.
                  <Button
                    type="primary"
                    disabled={!canEditPhone}
                    onClick={() => setA2pDrawerOpen(true)}
                  >
                    Register
                  </Button>
                </Flex>
              }
            />
          ) : twilioAccount.a2pStatus === 'approved' ? (
            <Alert
              style={{ marginBottom: 24 }}
              showIcon
              type="success"
              title="A2P 10DLC registration approved"
              description="Your brand and campaign are fully registered. SMS is ready to send."
            />
          ) : (
            <Flex vertical gap={16} style={{ marginBottom: 24 }}>
              <A2pStatusTimeline account={twilioAccount} />
              {twilioAccount.a2pStatus === 'rejected' && (
                <Alert
                  type="error"
                  showIcon
                  title="Registration could not be submitted"
                  description="We could not send your registration to Twilio. Check your details and try again."
                />
              )}
              {twilioAccount.a2pStatus === 'brand_approved' && (
                <Alert
                  type="info"
                  showIcon
                  title="Brand approved"
                  description="Your brand has been verified. Open the form and submit to complete campaign registration."
                />
              )}
              {(twilioAccount.a2pStatus === 'brand_failed' ||
                twilioAccount.a2pStatus === 'campaign_failed' ||
                twilioAccount.a2pStatus === 'brand_approved' ||
                twilioAccount.a2pStatus === 'rejected') && (
                <Flex gap={8}>
                  <Button
                    type="primary"
                    disabled={!canEditPhone}
                    onClick={() => setA2pDrawerOpen(true)}
                  >
                    {twilioAccount.a2pStatus === 'brand_approved'
                      ? 'Continue registration'
                      : 'Update registration'}
                  </Button>
                  {(twilioAccount.a2pStatus === 'brand_failed' ||
                    twilioAccount.a2pStatus === 'campaign_failed' ||
                    twilioAccount.a2pStatus === 'rejected') && (
                    <Button
                      onClick={() => {
                        Gleap.attachCustomData({
                          ...buildInfo.gleapCustomData,
                          organizationId,
                          a2pStatus: twilioAccount.a2pStatus,
                          ...(twilioAccount.a2pFailureReasons?.length
                            ? { a2pFailureReasons: twilioAccount.a2pFailureReasons.join('; ') }
                            : {}),
                        });
                        Gleap.open();
                      }}
                    >
                      Contact support
                    </Button>
                  )}
                </Flex>
              )}
            </Flex>
          ))}
        {numbers &&
          !numbers.some((n) => n.assignedOrganizationMembershipId === selectedMembership.id) && (
            <Alert
              style={{ marginBottom: 24 }}
              showIcon
              type="warning"
              title="No phone number assigned to you"
              description={`Without one you can't place or receive calls, send or receive SMS, get voicemail, or be the sender of AI agent SMS steps. ${
                canEditPhone
                  ? 'Assign yourself a number below.'
                  : 'Ask an admin to assign you a number.'
              }`}
            />
          )}
        {!numbers?.length ? (
          <Empty
            description="Buy your first phone number to start calling and texting."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          >
            <Button type="primary" disabled={!canBuyNumbers} onClick={() => setBuyDrawerOpen(true)}>
              Buy Number
            </Button>
          </Empty>
        ) : (
          <List
            items={numbers}
            renderItem={(pn) => {
              const assignedName = pn.assignedMembership
                ? `${
                    `${pn.assignedMembership.user?.firstName ?? ''} ${pn.assignedMembership.user?.lastName ?? ''}`.trim() ||
                    pn.assignedMembership.user?.email
                  }${pn.assignedMembership.id === selectedMembership.id ? ' (you)' : ''}`
                : null;
              return {
                key: pn.id,
                reactNode: (
                  <Record
                    left={
                      <Phone size="1em" style={{ color: 'var(--ant-color-text-quaternary)' }} />
                    }
                    title={
                      <Flex align="center" gap={8}>
                        <span>{formatPhoneNumber(pn.phoneNumber)}</span>
                        {pn.isDefault && <Tag>Default</Tag>}
                      </Flex>
                    }
                    description={assignedName ?? 'Unassigned'}
                    right={
                      <Flex
                        gap={8}
                        justify="flex-end"
                        vertical={stackTwilioNumberActions}
                        wrap={!stackTwilioNumberActions}
                        align={stackTwilioNumberActions ? 'stretch' : 'center'}
                      >
                        <Select
                          style={
                            stackTwilioNumberActions
                              ? { width: '100%', maxWidth: 360 }
                              : { minWidth: 200 }
                          }
                          placeholder="Assign to…"
                          value={pn.assignedOrganizationMembershipId ?? undefined}
                          disabled={!canEditPhone}
                          allowClear
                          options={memberOptions}
                          onChange={(value) => {
                            assignNumber.mutate({
                              organizationId,
                              phoneNumberId: pn.id,
                              data: {
                                organizationMembershipId: value ?? null,
                              },
                            });
                          }}
                        />
                        {canEditPhone && (
                          <Dropdown
                            trigger={['click']}
                            placement="bottomRight"
                            menu={{
                              items: [
                                ...(!pn.isDefault
                                  ? [
                                      {
                                        key: 'set-default',
                                        icon: <Star size="1em" />,
                                        label: 'Set as default',
                                        onClick: () =>
                                          setDefaultNumber.mutate({
                                            organizationId,
                                            phoneNumberId: pn.id,
                                          }),
                                      },
                                    ]
                                  : []),
                                {
                                  key: 'deactivate',
                                  icon: <PhoneOff size="1em" />,
                                  label: 'Deactivate',
                                  danger: true,
                                  onClick: () => {
                                    Modal.confirm({
                                      title: 'Deactivate number?',
                                      content:
                                        'This number will be released permanently and stop receiving calls and SMS.',
                                      okText: 'Deactivate',
                                      okButtonProps: { danger: true },
                                      onOk: () => {
                                        deactivateNumber.mutate({
                                          organizationId,
                                          phoneNumberId: pn.id,
                                        });
                                      },
                                    });
                                  },
                                },
                              ],
                            }}
                          >
                            <Button type="text" icon={<Ellipsis size="1em" />} />
                          </Dropdown>
                        )}
                      </Flex>
                    }
                  />
                ),
              };
            }}
          />
        )}
      </SettingsLayoutContent>

      {isA2pEnabled && (
        <A2pRegistrationDrawer
          organizationId={organizationId}
          open={a2pDrawerOpen}
          onClose={() => setA2pDrawerOpen(false)}
          a2pStatus={twilioAccount?.a2pStatus}
          initialSnapshot={twilioAccount?.a2pSubmissionSnapshot}
          a2pPaidVolumeTier={twilioAccount?.a2pPaidVolumeTier ?? null}
        />
      )}

      <Drawer
        title="Buy a phone number"
        open={buyDrawerOpen}
        onClose={closeBuyDrawer}
        width={480}
        destroyOnClose
      >
        <Flex vertical gap={16}>
          <Typography.Text strong>Search criteria</Typography.Text>
          <div
            style={{
              width: '100%',
              display: 'flex',
              flexDirection: screens.sm ? 'row' : 'column',
              alignItems: 'stretch',
              gap: 8,
            }}
          >
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'stretch',
                border: '1px solid var(--ant-color-border)',
                borderRadius: 8,
                overflow: 'hidden',
                background: 'var(--ant-color-bg-container)',
              }}
            >
              <Select
                value={searchCriteria}
                style={{ width: 160 }}
                bordered={false}
                options={[
                  { value: 'locality', label: 'Locality' },
                  { value: 'areaCode', label: 'Area code' },
                ]}
                onChange={(value) => {
                  setSearchCriteria(value as SearchCriteria);
                  setSearchInput('');
                }}
              />
              <div
                style={{
                  width: 1,
                  background: 'var(--ant-color-border)',
                }}
              />
              <Input
                placeholder={searchCriteria === 'locality' ? 'e.g. New York or TX' : 'e.g. 415'}
                value={searchInput}
                bordered={false}
                style={{ flex: 1, minWidth: 0 }}
                onChange={(e) => {
                  if (searchCriteria === 'areaCode') {
                    setSearchInput(e.target.value.replace(/\D/g, '').slice(0, 3));
                    return;
                  }
                  setSearchInput(e.target.value);
                }}
                onPressEnter={runSearch}
                allowClear
              />
            </div>
            <Button
              type="primary"
              disabled={!isValidInput}
              loading={searchQuery.isFetching}
              onClick={runSearch}
              style={screens.sm ? { minWidth: 96 } : { width: '100%' }}
            >
              Search
            </Button>
          </div>
          <Typography.Text type="secondary">
            {searchCriteria === 'locality'
              ? 'Enter a full city name (e.g. New York) or a US state, by code or name (e.g. TX or Texas). Partial city names usually do not return results.'
              : 'Enter a US area code (3 digits), e.g. 415.'}
          </Typography.Text>
          {searchQuery.isFetching ? (
            <Loader />
          ) : submitted ? (
            available.length ? (
              <div
                style={{
                  opacity: isStale ? 0.4 : 1,
                  pointerEvents: isStale ? 'none' : 'auto',
                  transition: 'opacity 120ms',
                }}
              >
                <List
                  items={available}
                  renderItem={(item) => ({
                    key: item.phoneNumber,
                    reactNode: (
                      <Record
                        left={<Phone size="1em" />}
                        title={formatPhoneNumber(item.phoneNumber)}
                        description={formatAvailableNumberLocation(item.locality, item.region)}
                        right={
                          <Flex align="center" gap={10}>
                            <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
                              {formatAvailableNumberPrice(item.monthlyPrice, item.priceUnit)}
                            </Typography.Text>
                            <Button
                              type="primary"
                              size="small"
                              loading={
                                provisionNumber.isPending &&
                                provisionNumber.variables?.data.phoneNumber === item.phoneNumber
                              }
                              onClick={() => {
                                provisionNumber.mutate({
                                  organizationId,
                                  data: {
                                    phoneNumber: item.phoneNumber,
                                    assignedOrganizationMembershipId: selectedMembership.id,
                                  },
                                });
                              }}
                            >
                              Buy
                            </Button>
                          </Flex>
                        }
                      />
                    ),
                  })}
                />
              </div>
            ) : (
              <Typography.Text type="secondary">
                No numbers found for "{submitted.value}".{' '}
                {submitted.criteria === 'locality'
                  ? 'Try the full city name, or a state instead.'
                  : 'Try another US area code.'}
              </Typography.Text>
            )
          ) : null}
        </Flex>
      </Drawer>
    </>
  );
};

export const Route = createFileRoute('/_authenticated/_app/settings/phone')({
  beforeLoad: ({ context }) => {
    if (!context.permissions?.phone?.can.view) {
      throw redirect({ to: '/settings' });
    }
  },
  component: SettingsPhoneRoute,
});
